/**
 * Composable для работы с локализованными строками
 *
 * Резолвит LocalizableString в строку на основе текущего языка
 */

import { type ComputedRef, computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LocalizableString, LocalizedString } from '@/types/theme'

/**
 * Проверяет, является ли значение LocalizedString объектом
 */
export function isLocalizedString(value: LocalizableString | undefined): value is LocalizedString {
  return typeof value === 'object' && value !== null && 'ru' in value
}

/**
 * Резолвит LocalizableString в строку для указанного языка
 * Fallback: locale -> 'ru' -> 'en' -> первое доступное значение
 */
export function resolveLocalizedString(
  value: LocalizableString | undefined,
  locale: string
): string {
  if (!value) return ''
  if (typeof value === 'string') return value

  // Попробовать запрошенный язык
  if (value[locale]) return value[locale] as string

  // Fallback на русский
  if (value.ru) return value.ru

  // Fallback на английский
  if (value.en) return value.en

  // Вернуть первое доступное значение
  const firstValue = Object.values(value).find((v) => typeof v === 'string' && v)
  return (firstValue as string) || ''
}

/**
 * Composable для реактивного получения локализованной строки
 *
 * @example
 * const { localized } = useLocalizedString()
 * const orgName = localized(org.name) // ComputedRef<string>
 *
 * @example
 * const { resolve } = useLocalizedString()
 * const name = resolve(org.name) // string (не реактивный)
 */
export function useLocalizedString() {
  const { locale } = useI18n()

  /**
   * Реактивно резолвит LocalizableString
   * Автоматически обновляется при смене языка
   */
  function localized(value: MaybeRefOrGetter<LocalizableString | undefined>): ComputedRef<string> {
    return computed(() => resolveLocalizedString(toValue(value), locale.value))
  }

  /**
   * Нереактивный резолв LocalizableString
   * Для использования вне шаблонов
   */
  function resolve(value: LocalizableString | undefined): string {
    return resolveLocalizedString(value, locale.value)
  }

  return {
    localized,
    resolve,
    locale,
  }
}

export default useLocalizedString
